<template>
  <Head title="Avid - Password Reset Form" />
  
  <div class="container-fluid d-flex flex-column h-100 onboarding-container">
    <div class="row m-4 justify-content-center onboarding-new align-items-stretch">
        <div class="col-lg-9 col-12">
            <div class="row h-100 justify-content-center align-items-center flex-grow-1">
                <div class="container-fluid col-12">
                    <Link class="navbar-brand" href="/">
                        <img src="/img/logo_png/avid_logo_black.png" alt="Avid" class="navbar-brand-logo-avidonly px-2">
                    </Link>
                </div>
                <div class="registration-form-container col-lg-5 col-12 mt-5 mt-lg-0 d-flex flex-column justify-content-center align-items-stretch">
                    <Link href="/" class="d-block w-100 text-center">
                        <img src="/img/logo_png/logo_tile.png" alt="Avid" class="mb-3" style="width: 50px; height: 50px;">
                    </Link>
                    <h2 class="text-center mb-4">Reset Password</h2>

                    <form method="POST" action="/password/reset" id="reset-password-form" @submit="handleSubmit">
                        <input type="hidden" name="_token" :value="csrfToken">
                        <input type="hidden" name="token" :value="token">
                        <input type="hidden" name="email" :value="email">

                        <div class="mb-3">
                            <input 
                                id="email" 
                                type="email" 
                                placeholder="Email" 
                                class="form-control" 
                                :class="{ 'is-invalid': errors.email }" 
                                name="email" 
                                :value="email" 
                                required 
                                autocomplete="email" 
                                autofocus aria-label="Email">
                            <span v-if="errors.email" class="invalid-feedback" role="alert">
                                <strong>{{ errors.email }}</strong>
                            </span>
                        </div>

                        <div class="mb-3">
                            <password-visibility 
                                name="password"
                                placeholder="Enter Password"
                                required
                                autocomplete="new-password"
                                :has-error="!!errors.password || !!passwordError"
                                :error-message="errors.password || passwordError || ''"
                                v-model="form.password"
                                @change="validatePassword(form.password)">
                            </password-visibility>
                            <div v-if="passwordError" class="text-danger invalid-feedback d-block">
                                <span v-html="passwordError"></span><br>
                                <em class="small">(Password must be at least 8 characters long, complex enough to be hard to guess, and avoid common patterns and sequences)</em>
                            </div>
                        </div>

                        <div class="mb-3">
                            <password-visibility
                                name="password_confirmation"
                                placeholder="Confirm Password"
                                required
                                autocomplete="new-password"
                                :show-inline-error="false"
                                v-model="form.password_confirmation"
                                @change="validatePasswordMatch()">
                            </password-visibility>
                            <div v-if="passwordConfirmError" class="text-danger invalid-feedback d-block">
                                {{ passwordConfirmError }}
                            </div>
                        </div>

                        <button type="submit" class="btn btn-primary w-100 mt-4" id="reset-button" :disabled="!canSubmit">
                            Reset Password
                        </button>
                    </form>
                </div>
            </div>
        </div>

        <div class="col-lg-3 d-none d-lg-block bg-with-image"></div>
    </div>
  </div>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { computed, ref, reactive } from 'vue';

const props = defineProps({
  token: {
    type: String,
    required: true
  },
  email: {
    type: String,
    default: ''
  },
  errors: {
    type: Object,
    default: () => ({})
  }
});

const csrfToken = computed(() => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'));

const form = reactive({
  password: '',
  password_confirmation: ''
});

const passwordError = ref('');
const passwordConfirmError = ref('');

const canSubmit = computed(() => {
  return form.password && 
         form.password_confirmation && 
         form.password === form.password_confirmation && 
         !passwordError.value;
});

const validatePassword = (password) => {
  if (!password) {
    passwordError.value = '';
    return;
  }

  window.axios.post('/api/user/validate_password', {
    password: password
  })
  .then(response => {
    if (!response.data.valid) {
      passwordError.value = response.data.errors.join(' ');
    } else {
      passwordError.value = '';
      validatePasswordMatch();
    }
  })
  .catch(error => {
    console.error('Password validation error:', error);
    passwordError.value = 'Error validating password. Please try again.';
  });
};

const validatePasswordMatch = () => {
  if (form.password_confirmation && form.password !== form.password_confirmation) {
    passwordConfirmError.value = 'The password entered does not match the one above.';
  } else {
    passwordConfirmError.value = '';
  }
};

const handleSubmit = (event) => {
  if (!canSubmit.value) {
    event.preventDefault();
    return false;
  }
  // Form will submit normally
  return true;
};
</script>

